import { temperatureToRgba } from './colormap';
import {
  computeRelativeTemperatureScale,
  IRON_RED_PERCENTILE_HIGH,
  IRON_RED_PERCENTILE_LOW
} from './relativeScale';

/**
 * 2D 온도 그리드 T[y][x] → Iron Red IR 이미지 (canvas).
 *
 * 자동 상대스케일(권장):
 *   lo = percentile(T, 0.5), hi = percentile(T, 99.5)
 *   u = clamp((T-lo)/(hi-lo), 0, 1) → IronRedLUT[round(u*255)]
 *
 * 좌표계: T[y][x] ↔ 픽셀 (x, y) 1:1
 */
export function renderThermalHeatmapToCanvas(canvas, dataMap, options = {}) {
  if (!canvas || typeof canvas.getContext !== 'function') {
    return null;
  }
  if (!Array.isArray(dataMap) || dataMap.length === 0 || !Array.isArray(dataMap[0])) {
    return null;
  }

  const height = dataMap.length;
  const width = dataMap[0].length;
  if (width <= 0 || height <= 0) {
    return null;
  }

  const { min, max, scaleMode } = resolveHeatmapScale(dataMap, options);
  if (!Number.isFinite(min) || !Number.isFinite(max)) {
    return null;
  }

  const alpha = options.alpha != null ? options.alpha : 200;
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    return null;
  }

  const imageData = ctx.createImageData(width, height);
  const buf = imageData.data;

  for (let y = 0; y < height; y += 1) {
    const row = dataMap[y];
    for (let x = 0; x < width; x += 1) {
      const v = row ? row[x] : NaN;
      const [r, g, b, a] = Number.isFinite(v)
        ? temperatureToRgba(v, min, max, alpha)
        : [0, 0, 0, 0];
      const i = (y * width + x) * 4;
      buf[i] = r;
      buf[i + 1] = g;
      buf[i + 2] = b;
      buf[i + 3] = a;
    }
  }

  ctx.putImageData(imageData, 0, 0);
  return { width, height, min, max, scaleMode };
}

/**
 * Offscreen 없이 ImageData만 생성 (테스트/다른 렌더러용)
 * @param {number[][]} dataMap
 * @param {{
 *   min?: number,
 *   max?: number,
 *   alpha?: number,
 *   scaleMode?: 'relative' | 'manual',
 *   percentileLow?: number,
 *   percentileHigh?: number
 * }} [options]
 * @returns {{ data: Uint8ClampedArray, width: number, height: number, min: number, max: number, scaleMode: string } | null}
 */
export function buildThermalHeatmapImageData(dataMap, options = {}) {
  if (!Array.isArray(dataMap) || dataMap.length === 0 || !Array.isArray(dataMap[0])) {
    return null;
  }
  const height = dataMap.length;
  const width = dataMap[0].length;
  const { min, max, scaleMode } = resolveHeatmapScale(dataMap, options);
  if (!Number.isFinite(min) || !Number.isFinite(max)) {
    return null;
  }

  const alpha = options.alpha != null ? options.alpha : 200;
  const data = new Uint8ClampedArray(width * height * 4);
  for (let y = 0; y < height; y += 1) {
    const row = dataMap[y];
    for (let x = 0; x < width; x += 1) {
      const v = row ? row[x] : NaN;
      const [r, g, b, a] = Number.isFinite(v)
        ? temperatureToRgba(v, min, max, alpha)
        : [0, 0, 0, 0];
      const i = (y * width + x) * 4;
      data[i] = r;
      data[i + 1] = g;
      data[i + 2] = b;
      data[i + 3] = a;
    }
  }
  return { data, width, height, min, max, scaleMode };
}

function resolveHeatmapScale(dataMap, options = {}) {
  const requested = options.scaleMode === 'manual' ? 'manual' : 'relative';
  let min = Number(options.min);
  let max = Number(options.max);

  // 고정(리포트 통일): 호출측 lo/hi
  if (requested === 'manual' && Number.isFinite(min) && Number.isFinite(max)) {
    return { min, max, scaleMode: 'manual' };
  }

  // 자동(권장): percentile 0.5 ~ 99.5
  const scale = computeRelativeTemperatureScale(dataMap, {
    percentileLow:
      options.percentileLow != null
        ? options.percentileLow
        : IRON_RED_PERCENTILE_LOW,
    percentileHigh:
      options.percentileHigh != null
        ? options.percentileHigh
        : IRON_RED_PERCENTILE_HIGH
  });
  if (!scale) {
    return { min: NaN, max: NaN, scaleMode: 'relative' };
  }
  return { min: scale.lo, max: scale.hi, scaleMode: 'relative' };
}
